import { Box, Button, createListCollection, For, Grid, Text, VStack } from '@chakra-ui/react';
import { CustomSelect } from '@components/ui/select/Select';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { selectActiveUser } from '@redux/authent/authent.selectors';
import { selectJammingModuleById } from '@redux/situation/monitoring.selectors';
import { selectDrones } from '@redux/situation/situation.selectors';
import { usePostGroundStationCommandMutation } from '@services/c2/c2.api';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { FireCommand, GroundStationCommandEnum, Target } from '@/types/c2/c2.types';
import { TargetLocation } from '@/types/sensor/sensor.types';
import { JamTypeEnum, TrackLight } from '@/types/sensor/sensors';

type Props = {
  target: Target<TargetLocation>;
  groundStationTrack: TrackLight;
};

export default function DroneControlPayload({ target, groundStationTrack }: Readonly<Props>) {
  const jammingModule = useSelectorWithReplayMode(selectJammingModuleById, target.id);
  const jammingTargetId = target.technicalData?.jammingTargetId ?? null;
  const drones = useSelectorWithReplayMode(selectDrones);
  const [modes, setModes] = useState<Set<JamTypeEnum>>(new Set());
  const [selectedTarget, setSelectedTarget] = useState<string | undefined>(jammingTargetId ?? undefined);
  const [postCommand, { isLoading }] = usePostGroundStationCommandMutation();
  const { activeSite } = useAppSelector(selectActiveUser);
  const site = activeSite?.code ?? '';
  const appCode = groundStationTrack.appCode;

  const isJamming = jammingTargetId !== null;

  function handleJam() {
    postCommand({
      site,
      appCode,
      command: {
        type: GroundStationCommandEnum.FIRE,
        targetId: selectedTarget!,
        jamType: Array.from(modes),
        sensorCode: groundStationTrack.sensorCode,
        systemId: target.technicalData!.systemId,
        componentId: target.technicalData!.autopilotId,
      } as FireCommand,
    });
  }

  function handleStopJam() {
    postCommand({
      site,
      appCode,
      command: {
        type: GroundStationCommandEnum.FIRE,
        targetId: null,
        jamType: Array.from(modes),
        sensorCode: groundStationTrack.sensorCode,
        systemId: target.technicalData!.systemId,
        componentId: target.technicalData!.autopilotId,
      } as FireCommand,
    });
  }

  const items = createListCollection({
    items: drones
      .filter((drone) => drone.id !== target.id)
      .map((drone) => ({
        value: drone.id,
        label: drone.displayId ?? target.id,
      })),
  });

  return (
    <VStack gap={0.25} width="100%" backgroundColor="neutral.900">
      {jammingModule && (
        <VStack width="100%" gap={0}>
          <Box padding={3} width="100%" backgroundColor="neutral.600">
            <Text padding={0.5} textStyle="md">
              {jammingModule.name}
            </Text>
          </Box>
          <VStack padding={4} width="100%" backgroundColor="neutral.850" gap={3} alignItems="start">
            <Text textStyle="lg">
              <FormattedMessage id="droneControl.tab.payloads.jammingCommands" />
            </Text>
            <VStack gap={1.5} width="100%" alignItems="start">
              <Text textStyle="md" color="neutral.300">
                <FormattedMessage id="droneControl.tab.payloads.jammingMode" />
              </Text>
              <Grid templateColumns="repeat(3, 1fr)" gap={1} width="100%">
                <For each={Object.values(JamTypeEnum)}>
                  {(type) => (
                    <Button
                      variant="toggle"
                      size="toggleM"
                      key={type}
                      onClick={() => {
                        const temp = new Set(modes);
                        if (temp.has(type)) {
                          temp.delete(type);
                        } else {
                          temp.add(type);
                        }
                        setModes(temp);
                      }}
                      data-active={modes.has(type)}
                    >
                      <Text>{type}</Text>
                    </Button>
                  )}
                </For>
              </Grid>
              <CustomSelect
                collection={items}
                value={selectedTarget}
                onChange={(value) => value !== null && setSelectedTarget(value)}
              />
            </VStack>
            <Button
              variant="primary"
              alignSelf="center"
              disabled={(!isJamming && modes.size === 0) || selectedTarget === undefined}
              loading={isLoading}
              onClick={() => {
                if (target.technicalData) {
                  if (isJamming) {
                    handleStopJam();
                  } else if (modes.size !== 0 && selectedTarget !== undefined) {
                    handleJam();
                  }
                }
              }}
            >
              <FormattedMessage id="droneControl.tab.payloads.jam" values={{ isJamming }} />
            </Button>
          </VStack>
        </VStack>
      )}
    </VStack>
  );
}
